import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo, useRef } from 'react'
import { useAppContext } from '../redux/slices/appContext'
import { postNamedCommand, postNamedQuery } from '../service'
import { throwObject } from '../utils/throwObject'
import { COMPONENT_DOCS_STORAGE_KEY } from '../constants'
import { QUERY_KEYS } from './queryKeys'

export function cleanJsx (rawJsx) {
  if (!rawJsx) return null
  return rawJsx
    .replace(/^\n|\n$/g, '')
    .replace(/(\r\n|\n|\r)/gm, '')
    .replace(/\s+/g, ' ')
    .replace(/>\s+/g, '>')
    .replace(/\s+</g, '<')
    .replace(';', '')
    .trim()
}

export const useAbundanceEngineViewByPathName = (viewPath, fallbackPath) => {
  const { userId } = useAppContext()
  return useQuery({
    queryKey: ['abundanceEngineViewByPathName', userId, viewPath, fallbackPath],
    queryFn: async () => {
      let abundanceView = null
      try {
        abundanceView = await postNamedQuery('abundanceEngine', 'get-view-by-path-v2', { path: viewPath })
      } catch (err) {
        console.log('Unable to find view', viewPath)
      }
      if (!abundanceView && fallbackPath) {
        abundanceView = await postNamedQuery('abundanceEngine', 'get-view-by-path-v2', { path: fallbackPath })
      }

      if (abundanceView?.response?.status === 204) {
        throwObject({
          message: 'View not found',
          code: 404
        })
      }

      return abundanceView?.data || null
    },
    select: (data) => {
      if (!data) return null
      // const { name, content, defaultProps } = data
      const { view, disabledView } = data
      const _view = {
        name: view.name,
        defaultProps: view.defaultProps,
        jsxView: cleanJsx(view.content)
      }
      const _disabledView = disabledView ? {
        name: disabledView.name,
        defaultProps: disabledView.defaultProps,
        jsxView: cleanJsx(disabledView.content)
      } : _view

      return {
        view: _view,
        disabledView: _disabledView
      }
    }
  })
}

export const usePreloadAbundanceEngineView = (viewPath, fallbackPath) => {
  const { userId } = useAppContext()
  const queryClient = useQueryClient()
  const preload = useCallback(async () => {
    await queryClient.prefetchQuery({
      queryKey: ['abundanceEngineViewByPathName', userId, viewPath, fallbackPath],
      queryFn: async () => {
        let abundanceView = null
        try {
          abundanceView = await postNamedQuery('abundanceEngine', 'get-view-by-path-v2', { path: viewPath })
        } catch (err) {
          console.log('Unable to find view', viewPath)
        }
        if (!abundanceView && fallbackPath) {
          abundanceView = await postNamedQuery('abundanceEngine', 'get-view-by-path-v2', { path: fallbackPath })
        }

        return abundanceView?.data || null
      }
    })
  }, [queryClient, userId, viewPath, fallbackPath])

  return { preload }
}

export const useClientViewEnablement = (viewPath, clientId) => {
  return useQuery({
    queryKey: ['abundance-engine/get-client-view-enablement', viewPath, clientId],
    queryFn: async () => {
      const { data } = await postNamedQuery('abundanceEngine', 'get-client-view-enablement', {
        path: viewPath,
        clientId
      })

      return !!data?.enablement?.enabled
    }
  })
}

/** TODO - need to make this endpoint */
export const useAdvisorViewTemplateConfig = (templateConfig) => {
  return useMemo(() => {
    if (templateConfig === 'default') {
      return ({
        data: {
          config: {
            sections: [
              { name: 'Advisors', type: 'group', levelTypeId: 301 }
            ]
          }
        },
        isLoading: false
      })
    }

    return {
      data: templateConfig,
      isLoading: false
    }
  }, [templateConfig])
}

export const useAbundanceEngineViews = (query, queryOptions = {}) => {
  const { userId } = useAppContext()
  const { mapper } = queryOptions
  return useQuery({
    queryKey: [QUERY_KEYS.abundanceEngineViews, userId],
    queryFn: async () => {
      const { data } = await postNamedQuery('abundanceEngine', 'get-active-views', {})
      return data
    },
    select: mapper
  })
}

export const useListAbundanceEngineViews = (query, queryOptions = {}) => {
  const { userId } = useAppContext()
  const { mapper } = queryOptions
  const queryKey = query ? JSON.stringify(query) : '{}'
  return useQuery({
    queryKey: [QUERY_KEYS.abundanceEngineViews, userId, queryKey],
    queryFn: async () => {
      const { data } = await postNamedQuery('abundanceEngine', 'list-views', query || {})
      return data
    },
    select: mapper
  })
}

export const useDeleteAbundanceEngineView = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (viewId) => {
      if (!viewId) throw new Error('viewId is required')
      await postNamedCommand('abundanceEngine', 'delete-view', { viewId })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.abundanceEngineViews] })
    }
  })
}

export const useDuplicateAbundanceEngineView = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      sourceViewId,
      path,
      name,
      description,
      isLive,
      showInNavigation,
      hideTitle,
      disabledViewId,
      enablementCodeId,
      viewTagIds
    }) => {
      if (!sourceViewId) throw new Error('sourceViewId is required')
      if (!path) throw new Error('path is required')
      if (!name) throw new Error('name is required')

      const { data } = await postNamedCommand('abundanceEngine', 'duplicate-view', {
        sourceViewId,
        path,
        name,
        description,
        isLive,
        showInNavigation,
        hideTitle,
        disabledViewId,
        enablementCodeId,
        viewTagIds
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.abundanceEngineViews], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.abundanceEngineViewByTags], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useAdvisorViewTemplates = () => {
  const { userId } = useAppContext()

  return useQuery({
    queryKey: [QUERY_KEYS.advisorViewTemplates, userId],
    queryFn: async () => {
      const { data } = await postNamedQuery('abundanceEngine', 'searchAdvisorViewTemplates', {
        includes: {
          scopes: true
        },
        sort: [{ field: 'hierarchyLevel', dir: 'desc' }]
      })

      return data
    }
  })
}

export const useAbundanceEngineViewContentById = viewId => {
  const { userId } = useAppContext()

  return useQuery({
    queryKey: [QUERY_KEYS.abundanceEngineViewContentById, userId, viewId],
    queryFn: async () => {
      const { data } = await postNamedQuery('abundanceEngine', 'getViewContentById', {
        viewId
      })

      return data
    }
  })
}

export const useAbundanceEngineGetViewPathDetails = ({ path, viewId }, queryOptions) => {
  const { userId } = useAppContext()

  const query = viewId ? { viewId } : { path }

  return useQuery({
    queryKey: [QUERY_KEYS.abundanceEngineViews, userId, JSON.stringify(query)],
    queryFn: async () => {
      const { data } = await postNamedQuery('abundanceEngine', 'get-view-edit-details', query)
      return data
    },
    ...queryOptions
  })
}

export const useAccountTearSheetView = accountId => {
  return useQuery({
    queryKey: ['abundanceEngine', QUERY_KEYS.accountTearSheet, accountId],
    queryFn: async () => {
      const { data } = await postNamedQuery('abundanceEngine', 'get-tear-sheet-view', {
        accountId
      })

      return data
    },
    select: (data) => {
      if (!data?.view) return null
      return {
        ...data.view,
        jsxView: data.view.content
          .replace(/^\n|\n$/g, '')
          .replace(/(\r\n|\n|\r)/gm, '')
          .replace(/\s+/g, ' ')
          .replace(/>\s+/g, '>')
          .replace(/\s+</g, '<')
          .replace(';', '')
          .trim()
      }
    }
  })
}

export const useAssetTearSheetView = assetId => {
  return useQuery({
    queryKey: ['abundanceEngine', QUERY_KEYS.assetTearSheet, assetId],
    queryFn: async () => {
      const { data } = await postNamedQuery('abundanceEngine', 'get-tear-sheet-view', {
        assetId
      })

      return data
    },
    select: (data) => {
      if (!data?.view) return null
      return {
        ...data.view,
        jsxView: data.view.content
          .replace(/^\n|\n$/g, '')
          .replace(/(\r\n|\n|\r)/gm, '')
          .replace(/\s+/g, ' ')
          .replace(/>\s+/g, '>')
          .replace(/\s+</g, '<')
          .replace(';', '')
          .trim()
      }
    }
  })
}

export const useAssetTearSheetViewById = tearSheetId => {
  return useQuery({
    queryKey: ['abundanceEngine', QUERY_KEYS.assetTearSheet, tearSheetId],
    enabled: !!tearSheetId,
    queryFn: async () => {
      const { data } = await postNamedQuery('abundanceEngine', 'get-tear-sheet-view-by-id', {
        tearSheetId
      })

      return data
    },
    select: (data) => {
      if (!data?.view) return null
      return {
        ...data.view,
        jsxView: data.view.content
          .replace(/^\n|\n$/g, '')
          .replace(/(\r\n|\n|\r)/gm, '')
          .replace(/\s+/g, ' ')
          .replace(/>\s+/g, '>')
          .replace(/\s+</g, '<')
          .replace(';', '')
          .trim()
      }
    }
  })
}

export const useAssetTearSheetList = (levelTypeId) => {
  return useQuery({
    queryKey: ['abundanceEngine', QUERY_KEYS.assetTearSheetList, levelTypeId],
    queryFn: async () => {
      const { data } = await postNamedQuery('abundanceEngine', 'get-tear-sheets', {
        levelTypeId
      })

      return data
    }
  })
}

export const useListEnablementCodes = () => {
  return useQuery({
    queryKey: ['abundanceEngine', QUERY_KEYS.listEnablementCodes],
    queryFn: async () => {
      const { data } = await postNamedQuery('abundanceEngine', 'list-enablement-codes', {
      })

      return data
    }
  })
}

export const useListViewTags = (body, queryOptions = {}) => {
  const { userId } = useAppContext()
  const queryKey = body ? JSON.stringify(body) : '{}'
  return useQuery({
    queryKey: [QUERY_KEYS.abundanceEngineViewTags, userId, queryKey],
    queryFn: async () => {
      const { data } = await postNamedQuery('abundanceEngine', 'list-view-tags', body)
      return data
    },
    enabled: true,
    ...queryOptions
  })
}

export const useGetViewsByTags = (body, queryOptions = {}) => {
  const { userId } = useAppContext()
  const queryKey = body ? JSON.stringify(body) : '{}'
  return useQuery({
    queryKey: [QUERY_KEYS.abundanceEngineViewByTags, userId, queryKey],
    queryFn: async () => {
      const { data } = await postNamedQuery('abundanceEngine', 'getViewsByTags', body)
      return data
    },
    enabled: true,
    ...queryOptions
  })
}

export const useUpdateViewTagViews = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ viewTagViews }) => {
      await postNamedCommand('abundanceEngine', 'update-view-tag-views', {
        viewTagViews
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.abundanceEngineViewTags], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.abundanceEngineViewByTags], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useAbundanceEngineBranding = (query, queryOptions = {}) => {
  const { userId } = useAppContext()
  const { mapper } = queryOptions
  const queryKey = query ? JSON.stringify(query) : '{}'

  return useQuery({
    queryKey: [QUERY_KEYS.abundanceEngineBranding, userId, queryKey],
    queryFn: async () => {
      const { data } = await postNamedQuery('abundanceEngine', 'get-branding', query || {})
      return data
    },
    select: mapper
  })
}

export const useSetBranding = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ brandKey, firmName, tagLine, logoUrl, faviconUrl }) => {
      const { data } = await postNamedCommand('abundanceEngine', 'set-branding', {
        brandKey, firmName, tagLine, logoUrl, faviconUrl
      })

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.abundanceEngineBranding], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useModifyBranding = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ brandKey, firmName, tagLine, logoUrl, faviconUrl }) => {
      const { data } = await postNamedCommand('abundanceEngine', 'modify-branding', {
        brandKey, firmName, tagLine, logoUrl, faviconUrl
      })

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.abundanceEngineBranding], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useGetDistinctBrandings = () => {
  return useQuery({
    queryKey: ['get-distinct-branding'],
    queryFn: async () => {
      const { data } = await postNamedQuery('abundanceEngine', 'get-distinct-branding', {})
      return data
    }
  })
}

export const useDeleteBrand = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (brandKey) => {
      if (!brandKey) throw new Error('brandKey is required')
      const { data } = await postNamedCommand('abundanceEngine', 'delete-brand', { brandKey })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.abundanceEngineBranding], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useListViewVersions = (viewId, { includeArchived = false } = {}, queryOptions = {}) => {
  return useQuery({
    queryKey: [QUERY_KEYS.abundanceEngineViewVersion, 'list', viewId, includeArchived],
    queryFn: async () => {
      const { data } = await postNamedQuery('abundanceEngine', 'list-view-versions', {
        viewId,
        includeArchived
      })
      return data
    },
    enabled: !!viewId,
    ...queryOptions
  })
}

export const useGetViewVersion = (viewVersionId, queryOptions = {}) => {
  return useQuery({
    queryKey: [QUERY_KEYS.abundanceEngineViewVersion, viewVersionId],
    queryFn: async () => {
      const { data } = await postNamedQuery('abundanceEngine', 'get-view-version', {
        viewVersionId
      })
      return data
    },
    enabled: !!viewVersionId,
    ...queryOptions
  })
}

export const useCreateViewVersion = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ viewId, content, description }) => {
      const { data } = await postNamedCommand('abundanceEngine', 'create-view-version', {
        viewId,
        content,
        description
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.abundanceEngineViewVersion], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useModifyViewVersion = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ viewVersionId, content, description }) => {
      const { data } = await postNamedCommand('abundanceEngine', 'modify-view-version', {
        viewVersionId,
        content,
        description
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.abundanceEngineViewVersion], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.abundanceEngineViews], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useSetLiveViewVersion = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ viewId, viewVersionId }) => {
      const { data } = await postNamedCommand('abundanceEngine', 'set-live-view-version', {
        viewId,
        viewVersionId
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.abundanceEngineViewVersion], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.abundanceEngineViews], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useArchiveViewVersion = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ viewVersionId }) => {
      const { data } = await postNamedCommand('abundanceEngine', 'archive-view-version', {
        viewVersionId
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.abundanceEngineViewVersion], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useUnarchiveViewVersion = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ viewVersionId }) => {
      const { data } = await postNamedCommand('abundanceEngine', 'unarchive-view-version', {
        viewVersionId
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.abundanceEngineViewVersion], refetchType: 'all' }).catch(console.error)
    }
  })
}

function getSessionDocs () {
  try {
    const raw = sessionStorage.getItem(COMPONENT_DOCS_STORAGE_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

function setSessionDocs (docs) {
  try {
    sessionStorage.setItem(COMPONENT_DOCS_STORAGE_KEY, JSON.stringify(docs))
  } catch (error) {
    console.error('Failed to set component docs in session storage', error)
  }
}

export const useComponentDocs = () => {
  const sessionCache = useRef(getSessionDocs())

  return useQuery({
    queryKey: [QUERY_KEYS.abundanceEngineComponentDocs],
    queryFn: async () => {
      if (sessionCache.current) {
        return sessionCache.current
      }

      const { data } = await postNamedQuery('abundanceEngine', 'get-component-docs', {})
      const docs = data?.docs || {}
      setSessionDocs(docs)
      sessionCache.current = docs
      return docs
    },
    staleTime: Infinity
  })
}

export const useComponentDoc = (componentName) => {
  const { data: allDocs, isLoading, isError, error } = useComponentDocs()

  const doc = useMemo(() => {
    if (!componentName || !allDocs) return null
    return allDocs[componentName] || null
  }, [componentName, allDocs])

  return { doc, isLoading, isError, error }
}
